import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import {
	JadeServerNotificationTypes,
	NotificationJsonModel,
	Types,
	WebSocketSubscriptionMsg,
} from '@siza-staff-care/shared/data';
import { assertDefined } from '@siza-staff-care/shared/utils';
import {
	EMPTY,
	Observable,
	Subject,
	catchError,
	combineLatest,
	exhaustMap,
	filter,
	finalize,
	map,
	of,
	switchMap,
	takeWhile,
	tap,
	timer,
	withLatestFrom,
} from 'rxjs';
import { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';

const RETRY_SECONDS = 5;
const MAX_RETRIES = 30;
const DEBUG_MODE = false;

type SubscriptionKey = string;

interface NotificationsState {
	baseUri: string;
	wsSubjectConfig?: WebSocketSubjectConfig<unknown>;
	subscribeUnsubscribeMessages: WebSocketSubscriptionMsg[];
	isConnected: boolean;
	socket?: WebSocketSubject<unknown>;
	subscriptionCount: number;
	connections: number;
	reconnectionTries: number;
	messagesReceived: number;
}

@Injectable({
	providedIn: 'root',
})
export class NotificationsService extends ComponentStore<NotificationsState> {
	/**
	 * Per-key counts of how many subscriptions are active.
	 */
	private subscriptionKeyCounts: Map<SubscriptionKey, number> = new Map();

	readonly isConnected$ = this.select(({ isConnected }) => isConnected);
	readonly subscriptionCount$ = this.select(({ subscriptionCount }) => subscriptionCount);
	readonly connections$ = this.select(({ connections }) => connections);
	readonly reconnectionTries$ = this.select(({ reconnectionTries }) => reconnectionTries);
	readonly messagesReceived$ = this.select(({ messagesReceived }) => messagesReceived);

	private messagesSubject = new Subject<unknown>();
	private messages$ = this.messagesSubject.asObservable(); //.pipe(share());

	private readonly baseUri$ = this.select(({ baseUri }) => baseUri);
	private readonly wsSubjectConfig$ = this.select(({ wsSubjectConfig }) => wsSubjectConfig);

	private readonly subscribeUnsubscribeMessages$ = this.select(
		({ subscribeUnsubscribeMessages }) => subscribeUnsubscribeMessages,
	);
	private readonly socket$ = this.select(({ socket }) => socket);

	/**
	 * A stream of messages to send, combined with whether the websocket is connected.
	 * This will emit when the websocket is connected, and there are messages to send.
	 */
	private readonly toSend$ = combineLatest([this.isConnected$, this.subscribeUnsubscribeMessages$]).pipe(
		filter(([isConnected, queue]) => isConnected && queue.length > 0),
		map(([, queue]) => queue),
	);

	/**
	 * Constructs the WebSocketSubjectConfig object, with open and close observers to handle connection status,
	 * and trying to re-connect when disconnected.
	 */
	private readonly setUpWebSocketSubjectConfig = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.baseUri$, this.connections$),
			tap(([, baseUri, connections]) => {
				const url = baseUri.replace(/^http/, 'ws') + 'staff-care.ws';
				const config: WebSocketSubjectConfig<unknown> = {
					url,
					closeObserver: {
						next: (event) => {
							DEBUG_MODE && console.log('closeObserver', event);
							this.patchState({ isConnected: false });
							this.tryReconnect();
						},
					},
					openObserver: {
						next: (event) => {
							DEBUG_MODE && console.log('openObserver', event);
							connections++;
							this.patchState({ isConnected: true, connections });
						},
					},
				};

				this.patchState({ wsSubjectConfig: config });
			}),
		),
	);

	/**
	 * Attempts to connect to the websocket.
	 */
	private readonly connect = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.wsSubjectConfig$, this.messagesReceived$),
			switchMap(([, config, messagesReceived]) => {
				assertDefined(config);

				// Create a new socket, and listen for messages, pushing them into the messagesSubject.
				const socket = new WebSocketSubject(config);
				this.patchState({ socket });
				return socket.pipe(
					tap((msg) => {
						messagesReceived++;
						this.patchState({ messagesReceived });

						this.messagesSubject.next(msg);
					}),
					catchError((err) => {
						DEBUG_MODE && console.log('error in connect', err);
						return EMPTY;
					}),
				);
			}),
		),
	);

	/**
	 * Handles attempting to reconnect to the websocket until connected or the max retries have been reached.
	 */
	private readonly tryReconnect = this.effect((trigger$) =>
		trigger$.pipe(
			exhaustMap(() => {
				return timer(RETRY_SECONDS * 1000).pipe(
					withLatestFrom(this.isConnected$, this.reconnectionTries$),
					takeWhile(([, isConnected, reconnectionTries], index) => {
						if (!isConnected) {
							DEBUG_MODE && console.log('Attempting re-connect to websocket - try #' + index + 1);

							reconnectionTries++;
							this.patchState({ reconnectionTries });
						}

						return !isConnected && index < MAX_RETRIES;
					}),
					tap(() => {
						this.connect();
					}),
				);
			}),
		),
	);

	/**
	 * Watches the queue for changes, and when the socket exists, sends the messages in the queue.
	 */
	readonly watchQueue = this.effect((queue$: Observable<WebSocketSubscriptionMsg[]>) =>
		queue$.pipe(
			withLatestFrom(this.socket$),
			tap(([queue, socket]) => {
				DEBUG_MODE && console.log('watchQueue', queue, socket);

				if (!socket) {
					return;
				}

				while (queue.length > 0) {
					const msg = queue.shift();
					assertDefined(msg);

					DEBUG_MODE && console.log('Sending queued message', msg);
					socket.next(msg);

					this.patchState({ subscribeUnsubscribeMessages: queue });
				}
			}),
		),
	);

	/**
	 * Adds a message to the queue to send to Jade to subscribe or unsubscribe to/from a notification.
	 */
	private readonly queueSubscribeUnsubscribeMessageIfUnique = this.effect(
		(msg$: Observable<WebSocketSubscriptionMsg>) =>
			msg$.pipe(
				withLatestFrom(this.subscribeUnsubscribeMessages$, this.subscriptionCount$),
				tap(([msg, queue, subscriptionCount]) => {
					let send = false;

					// Check whether we've already got this subscription active. If subscribing, don't subscribe if we already have it.
					// If unsubscribing, only unsubscribe if we are the last.
					const key = this.makeSubscriptionKey(msg);
					if (msg.beginSub) {
						const count = this.subscriptionKeyCounts.get(key) || 0;
						if (!count) {
							send = true;
						}

						this.subscriptionKeyCounts.set(key, count + 1);
					} else {
						const count = (this.subscriptionKeyCounts?.get(key) ?? 0) - 1;

						if (count < 0) {
							throw new Error(`Unsubscribe called for ${key} but no count found`);
						}

						if (!count) {
							send = true;
						}

						this.subscriptionKeyCounts.set(key, count);
					}

					if (!send) {
						return;
					}

					if (msg.beginSub) {
						subscriptionCount++;
					} else {
						subscriptionCount--;
					}

					this.patchState({ subscribeUnsubscribeMessages: [...queue, msg], subscriptionCount });
				}),
			),
	);

	constructor(
		@Inject(DOCUMENT) document: Document,
		private appState: AppStateService,
	) {
		super({
			baseUri: document.baseURI,
			subscribeUnsubscribeMessages: [],
			isConnected: false,
			subscriptionCount: 0,
			connections: 0,
			reconnectionTries: 0,
			messagesReceived: 0,
		});

		this.setUpWebSocketSubjectConfig();
		this.connect();
		this.watchQueue(this.toSend$);
	}

	/**
	 * Begins a Jade class notification, returning an Observable of the type.
	 * @param jadeClassName The Jade class name
	 * @param eventType The Jade event type number to subscribe to
	 * @param ignoreSelf Whether to ignore messages from the current session
	 * @returns
	 */
	beginClassNotification<T extends NotificationJsonModel = NotificationJsonModel>(opts: {
		jadeClassName: string;
		eventType: number;
		ignoreSelf?: boolean;
		transients?: boolean;
	}): Observable<T> {
		const { jadeClassName, eventType, ignoreSelf, transients } = opts;

		return this.setUpNotification<T>({ jadeClassName, eventType, transients }).pipe(
			filter((msg) => {
				if (ignoreSelf && msg.note_causer_sessionId === this.appState.settings.apiSessionId) {
					return false;
				}

				return true;
			}),
		);
	}

	/**
	 * Begins a Jade session notification, returning an Observable of the type.
	 * @param eventType The Jade event type number to subscribe to
	 * @returns
	 */
	beginSessionNotification<T extends NotificationJsonModel = NotificationJsonModel>(
		eventType: number,
	): Observable<T> {
		return this.setUpNotification({ eventType, session: true });
	}

	/**
	 * Sets up the notification in Jade, and returns an Observable of messages of the type.
	 * @param opts
	 * @returns
	 */
	private setUpNotification<T extends NotificationJsonModel = NotificationJsonModel>(opts: {
		jadeClassName?: string;
		eventType: number;
		session?: boolean;
		transients?: boolean;
	}) {
		let subMsg: WebSocketSubscriptionMsg;

		// Note - do all handling of the setup inside a stream, so that we don't do the subscription stuff until
		// the stream is subscribed to.
		return of(1).pipe(
			tap(() => {
				opts.jadeClassName ||= '';
				opts.session ||= false;

				const sessionOid = opts.session ? this.appState.settings.apiSessionId || '' : '';

				subMsg = {
					$type: Types.WebSocketSubscriptionMsg,
					className: opts.jadeClassName,
					eventType: opts.eventType,
					session: opts.session,
					oid: sessionOid,
					transients: !!opts.transients,
					beginSub: true,
				} as WebSocketSubscriptionMsg;

				// Send a message to Jade to subscribe to the notification.
				DEBUG_MODE && console.log('subscribing to notification', subMsg, opts);
				this.queueSubscribeUnsubscribeMessageIfUnique(subMsg);
			}),
			switchMap(() => {
				return this.messages$.pipe(
					map((msg) => msg as T),
					tap((msg) => {
						DEBUG_MODE && console.log('received notification', msg);
					}),
					filter((msg) => {
						DEBUG_MODE && console.log('filtering notification', msg, opts);
						let forUs = false;
						if (opts.session) {
							forUs = msg.note_eventType === opts.eventType && msg.note_typeName === 'ApiSession';
						} else {
							forUs =
								msg.note_typeName === opts.jadeClassName &&
								(msg.note_eventType === opts.eventType ||
									(opts.eventType === JadeServerNotificationTypes.Any_System_Event &&
										msg.note_sysNotification));
						}

						DEBUG_MODE && console.log('...forUs', forUs);
						return forUs;
					}),
					finalize(() => {
						// Caller has unsubscribed from the stream, so send the message to Jade to unsubscribe from the notification.
						const unsubscribeMessage = {
							...subMsg,
							beginSub: false,
						};

						DEBUG_MODE && console.log('unsubscribing to notification', unsubscribeMessage, opts);
						this.queueSubscribeUnsubscribeMessageIfUnique(unsubscribeMessage);
					}),
				);
			}),
		);
	}

	private makeSubscriptionKey(msg: WebSocketSubscriptionMsg) {
		return `${msg.className}-${msg.eventType}-${msg.session}-${msg.oid}`;
	}
}
